import React from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { expertDashboardApi } from '../../../lib/http';

interface TimeSlot {
  time: string;
  status: string;
}

interface AvailabilityResponse {
  view_mode: string;
  date: string;
  timezone: string;
  slots: TimeSlot[];
  working_hours: { start: string; end: string } | null;
  summary: Record<string, number>;
}

interface DateTimeSectionProps {
  date: Date | null;
  startTime: string | null;
  isPrefilled: boolean;
  error?: string;
  onChange: (date: Date | null, startTime: string | null) => void;
}

const DateTimeSection: React.FC<DateTimeSectionProps> = ({
  date,
  startTime,
  isPrefilled,
  error,
  onChange,
}) => {
  const dateString = date ? format(date, 'yyyy-MM-dd') : null;

  const { data: availability, isLoading: slotsLoading } = useQuery<AvailabilityResponse>({
    queryKey: ['expert-availability', dateString],
    queryFn: async () => {
      if (!dateString) throw new Error('No date');
      const response = await expertDashboardApi.calendar.getAvailability({
        view_mode: 'day',
        date: dateString,
      });
      return response.data;
    },
    enabled: !!dateString && !isPrefilled,
  });

  const availableSlots = availability?.slots?.filter(
    (slot) => slot.status === 'available'
  ) || [];

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value ? new Date(e.target.value) : null;
    // Clear time when date changes since available slots may differ
    onChange(newDate, null);
  };

  const handleSlotClick = (time: string) => {
    onChange(date, time);
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const formatDateDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'MMMM dd, yyyy');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Date & Time <span className="text-red-500">*</span>
      </label>

      {isPrefilled && date && startTime ? (
        // Read-only display when prefilled from slot click
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div className="flex-1">
              <div className="text-base font-semibold text-gray-900">
                {formatDateDisplay(date)}
              </div>
              <div className="text-base text-gray-700 mt-1">
                {startTime}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Pre-filled from calendar
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Editable inputs when opened from "+ New Booking" button
        <div className="space-y-3">
          {/* Date Picker */}
          <div>
            <label htmlFor="booking-date" className="block text-sm text-gray-600 mb-1">
              Date
            </label>
            <input
              type="date"
              id="booking-date"
              value={formatDateForInput(date)}
              onChange={handleDateChange}
              onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
              min={format(new Date(), 'yyyy-MM-dd')}
              className={`block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 cursor-pointer ${
                error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
          </div>

          {/* Time Slots */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">
              Time
            </label>

            {!date ? (
              <p className="text-sm text-gray-400">Select a date to see available times</p>
            ) : slotsLoading ? (
              <div className="flex items-center gap-2 py-2">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
                <span className="text-sm text-gray-500">Loading available times...</span>
              </div>
            ) : availableSlots.length === 0 ? (
              <p className="text-sm text-gray-500">No available time slots for this date</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {availableSlots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    onClick={() => handleSlotClick(slot.time)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                      startTime === slot.time
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {slot.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default DateTimeSection;
